import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Brain, Bot, Sparkles, FileSearch, ShieldCheck } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Slider } from "@/components/ui/slider";

const AISettings = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [aiFeatures, setAiFeatures] = useState({
    documentAnalysis: true,
    riskAssessment: true,
    fraudDetection: true,
    behaviorAnalysis: true,
    chatbot: true,
    imageRecognition: true,
  });

  const [confidenceThreshold, setConfidenceThreshold] = useState([0.7]);

  const handleSaveSettings = async () => {
    setIsLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      toast({
        title: "Settings Saved",
        description: "AI settings have been updated successfully.",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save settings. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Brain className="h-5 w-5" />
          AI Features Configuration
        </CardTitle>
        <CardDescription>Manage AI-powered features and capabilities</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label className="flex items-center gap-2">
                <FileSearch className="h-4 w-4" />
                Document Analysis
              </Label>
              <p className="text-sm text-muted-foreground">
                AI-powered document verification and analysis
              </p>
            </div>
            <Switch
              checked={aiFeatures.documentAnalysis}
              onCheckedChange={(checked) =>
                setAiFeatures({ ...aiFeatures, documentAnalysis: checked })
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4" />
                Risk Assessment
              </Label>
              <p className="text-sm text-muted-foreground">
                Advanced AI risk analysis system
              </p>
            </div>
            <Switch
              checked={aiFeatures.riskAssessment}
              onCheckedChange={(checked) =>
                setAiFeatures({ ...aiFeatures, riskAssessment: checked })
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label className="flex items-center gap-2">
                <Bot className="h-4 w-4" />
                AI Chatbot
              </Label>
              <p className="text-sm text-muted-foreground">
                Intelligent customer support chatbot
              </p>
            </div>
            <Switch
              checked={aiFeatures.chatbot}
              onCheckedChange={(checked) =>
                setAiFeatures({ ...aiFeatures, chatbot: checked })
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label className="flex items-center gap-2">
                <Sparkles className="h-4 w-4" />
                Image Recognition
              </Label>
              <p className="text-sm text-muted-foreground">
                AI-powered image analysis and verification
              </p>
            </div>
            <Switch
              checked={aiFeatures.imageRecognition}
              onCheckedChange={(checked) =>
                setAiFeatures({ ...aiFeatures, imageRecognition: checked })
              }
            />
          </div>

          <div className="space-y-4">
            <Label>AI Confidence Threshold</Label>
            <Slider
              value={confidenceThreshold}
              onValueChange={setConfidenceThreshold}
              max={1}
              step={0.1}
              className="w-full"
            />
            <p className="text-sm text-muted-foreground">
              Minimum confidence level for AI predictions: {confidenceThreshold[0] * 100}%
            </p>
          </div>
        </div>

        <Button
          onClick={handleSaveSettings}
          disabled={isLoading}
          className="w-full"
        >
          <Brain className="mr-2 h-4 w-4" />
          Save AI Settings
        </Button>
      </CardContent>
    </Card>
  );
};

export default AISettings;